import { Badge } from 'components/common-form/builder/form/node/field/MultipleBadgeField';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { SgTagsPicker } from 'components/shared/Tags';
import { useSPIntl } from 'hooks/useIntl';
import type { IntlKey } from 'locales';
import type { SimpleGridFieldMetadata } from 'models/forms/fields';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';
import type { RowData } from './SimpleGrid/GridColumns';

const StyledTagsPicker = styled(SgTagsPicker)`
  .item button[disabled] {
    display: none;
  }

  .item button[disabled] + span {
    margin-left: 0.5rem;
  }
`;

export function OrderedPickerField({ node: { id }, tabIndex, workspaceType }: ProductNodeBuilderProps) {
  const [data, setData] = useData<RowData[] | undefined>(id);
  const metadata = useFieldMetadata<SimpleGridFieldMetadata & Readonly<{ columnId: string }>>(id)!;
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const { formatMessage, newCollator } = useSPIntl();
  const { isReadOnly, columnId, columnFieldDefinitions, nextAvailableItems, translationKey } = metadata;

  const availableItems = useMemo(() => {
    if (isReadOnly) {
      return [];
    }
    const collator = newCollator();
    return nextAvailableItems
      .map(item => ({
        ...item,
        key: item.value,
        label: formatMessage({ id: `form.${item.label}` as IntlKey, defaultMessage: item.label }),
        forced: columnFieldDefinitions?.[item.value as any]?.isReadOnly || false,
      }))
      .sort((a, b) => collator.compare(a.label, b.label));
  }, [columnFieldDefinitions, formatMessage, isReadOnly, newCollator, nextAvailableItems]);

  const onSelection = useCallback(
    (selections: readonly string[]) => {
      const newData = selections.map(selection => ({ [columnId]: selection }));
      setData(newData);
    },
    [columnId, setData],
  );

  const badgeItems = useMemo(() => {
    if (!isReadOnly) {
      return [];
    }
    return data?.map(row => nextAvailableItems.find(x => x.value === row[columnId])) ?? [];
  }, [columnId, data, isReadOnly, nextAvailableItems]);

  if (isReadOnly) {
    return (
      <BlockOrientationFormGroup
        inputId={id}
        label={`form.${translationKey}` as IntlKey}
        workspaceType={workspaceType}
        className={patchedFieldClassName}
      >
        {badgeItems?.map(item => {
          return (
            <Badge
              key={item?.value}
              label={item?.label === undefined ? item?.value : formatMessage({ id: item?.label })}
              e2e={`badge-${id}`}
            />
          );
        })}
      </BlockOrientationFormGroup>
    );
  }

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as IntlKey}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      <StyledTagsPicker
        id={id}
        onSelectionKeysChanged={onSelection}
        singleSelect={false}
        available={availableItems}
        selection={(data ?? []).map(item => item[columnId]) as readonly string[]}
        noIcon
        noClear
        keepOrder
        placeholder={formatMessage({ id: 'form.picker.placeholder' })}
        maxDisplayedItems={availableItems.length}
        tabIndex={tabIndex}
      />
    </BlockOrientationFormGroup>
  );
}

OrderedPickerField.displayName = 'OrderedPickerField';
